// Reusable client-side table sort. For small/paginated lists; large data should
// move to server-side sort via the API's ?sort=&order= params.

import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}

/** Comparable shape: a string for text, number for numerics, null = "missing". */
type Cmp = string | number | null;

/**
 * Normalize an accessor's return value into something we can compare.
 * Dates → epoch ms. Booleans → 0/1. Arrays → length. Strings → lowercased.
 * `undefined` and `null` become `null` (sorts last).
 */
function normalize(v: unknown): Cmp {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v instanceof Date) return v.getTime();
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'string') {
    // ISO-8601 strings get compared as date strings, which still sort correctly
    // lexicographically — no special case needed.
    return v.toLowerCase();
  }
  // Fallback for objects, etc.
  try { return JSON.stringify(v).toLowerCase(); } catch { return String(v).toLowerCase(); }
}

function compare(a: Cmp, b: Cmp): number {
  if (a === b) return 0;
  if (a === null) return 1;   // nulls last regardless of direction
  if (b === null) return -1;
  return a < b ? -1 : 1;
}

export type Accessors<T> = Record<string, (row: T) => unknown>;

export function useTableSort<T>(
  rows: T[] | undefined | null,
  accessors: Accessors<T>,
  initial?: SortState,
) {
  const [sort, setSort] = useState<SortState | null>(initial ?? null);

  const sorted = useMemo(() => {
    if (!rows) return [] as T[];
    if (!sort) return rows;
    const accessor = accessors[sort.key];
    if (!accessor) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = normalize(accessor(a));
      const vb = normalize(accessor(b));
      const cmp = compare(va, vb);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, accessors]);

  return { sorted, sort, setSort };
}

/** Three-state cycle used by SortableTh: null → asc → desc → null. */
export function nextSortState(current: SortState | null, key: string): SortState | null {
  if (current?.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return null;
}

/**
 * Normalize a work-item key like "CTSK-12" into a sortable string.
 * Plain string compare on `w.key` puts "CTSK-10" before "CTSK-2" because
 * '1' < '2' at the same position — classic lex-vs-numeric trap. This
 * helper returns a value that:
 *
 *   (a) groups by the project prefix (all CTSK-* together, then CV-*),
 *   (b) sorts numerically WITHIN each project (CTSK-2 before CTSK-10).
 *
 * Done by zero-padding the trailing number to 12 digits (32-bit int max
 * is 10 digits; 12 is comfortable headroom). Keys without the standard
 * `<PREFIX>-<NUM>` shape pass through lowercased so the existing
 * lex-sort path still applies.
 *
 * Use in a useTableSort accessor:
 *
 *   key: (w: WorkItem) => naturalKey(w.key),
 */
export function naturalKey(key: string): string {
  const m = /^(.+?)-(\d+)$/.exec(key);
  if (!m) return key.toLowerCase();
  return `${m[1]!.toLowerCase()}-${m[2]!.padStart(12, '0')}`;
}
